// The ranked list. Suggest, approve, apply.
//
// A Watch sits in this list as its own answer rather than being hidden. That is
// most of what separates this from a fixed-rule tool: "no sales after ten
// clicks, blocked" is a guess wearing a decision's clothes, and "needs about
// sixty more clicks" is the truth.
//
// Nothing here writes to Amazon. Approving records a decision; the apply step is
// behind two locks and says so.
import React, { useState } from 'react';
import { FiCheck, FiEye, FiRotateCcw, FiX } from 'react-icons/fi';
import s from '../KeywordOptimiser.module.css';
import { FEATURE_LABEL, money, rupees2 } from '../format';

function Confidence({ value, parts }) {
  const title = Object.entries(parts || {})
    .filter(([, v]) => typeof v === 'number')
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${(v * 100).toFixed(0)}%`).join(' · ');
  return (
    <span className={s.confBar} title={title || undefined}>
      <span className={s.confTrack}>
        <span className={s.confFill} style={{ width: `${(value || 0) * 100}%` }} />
      </span>
      {Math.round((value || 0) * 100)}% confident
    </span>
  );
}

function Expected({ expected, currency }) {
  if (!expected) return null;
  const bits = [];
  if (expected.spend_saved_per_month != null) {
    bits.push(['Saves', money(expected.spend_saved_per_month, currency) + ' a month']);
  }
  if (expected.sales_lost_per_month) {
    bits.push(['Gives up', money(expected.sales_lost_per_month, currency)
      + ' of sales']);
  }
  if (expected.starting_bid != null) {
    bits.push(['Starting bid', rupees2(expected.starting_bid, currency)]);
  }
  if (expected.past_sales_per_month != null) {
    bits.push(['Has been earning', money(expected.past_sales_per_month, currency)
      + ' a month']);
  }
  if (expected.spend_moved_per_month != null) {
    bits.push(['Moves', money(expected.spend_moved_per_month, currency) + ' a month']);
  }
  if (expected.destination) bits.push(['Goes', expected.destination]);
  if (expected.words != null) {
    bits.push(['Words', `${expected.words} to ${expected.to_backend ? 'backend terms'
      : 'the listing'}`]);
  }
  if (expected.held_for_confirmation) {
    bits.push(['Held for you', `${expected.held_for_confirmation} brand-like`]);
  }
  if (!bits.length) return null;
  return (
    <div className={s.sugExpected}>
      {bits.map(([label, value]) => (
        <span key={label}>{label} <b>{value}</b></span>
      ))}
    </div>
  );
}

function Words({ action }) {
  const add = action?.add || [];
  const held = action?.confirm_first || [];
  if (!add.length && !held.length) return null;
  return (
    <div style={{ marginTop: 9 }}>
      {add.map((w) => (
        <span key={w.word} className={s.wordChip} title={`${w.because} · to ${w.to}`}>
          {w.word}
        </span>
      ))}
      {held.map((w) => (
        <span key={w.word} className={`${s.wordChip} ${s.wordChip_held}`}
              title={w.why_held}>
          {w.word} — check first
        </span>
      ))}
    </div>
  );
}

export default function Suggestions({ data, currency, onDecide, onApply, onUndo,
                                      onApproveMany, onIntentional, busy }) {
  const [feature, setFeature] = useState('all');
  const [showing, setShowing] = useState('open');
  const [selected, setSelected] = useState([]);

  const all = data?.suggestions || [];
  const open = all.filter((x) => x.status === 'Pending');
  const watching = all.filter((x) => x.status === 'Watch');
  const decided = all.filter((x) => ['Approved', 'Applied', 'Dismissed', 'Failed',
    'Expired', 'Undone'].includes(x.status));

  const pool = showing === 'open' ? open : showing === 'watch' ? watching : decided;
  const rows = pool.filter((x) => feature === 'all' || x.feature === feature);

  const features = ['all', 'negatives', 'harvest', 'conflicts', 'discovery', 'listing'];
  const counts = { all: open.length };
  features.slice(1).forEach((f) => {
    counts[f] = open.filter((x) => x.feature === f).length;
  });

  const toggle = (id) => setSelected((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  if (!all.length) {
    return (
      <p className={s.empty}>
        Nothing to work through. {data?.gates?.note}
      </p>
    );
  }

  return (
    <div>
      {data?.apply_note && (
        <div className={s.applyBanner}>
          <b>Approved changes are not being sent to Amazon.</b> {data.apply_note}
          {!!(data.apply_blocked_by || []).length && (
            <ul className={s.lockList}>
              {data.apply_blocked_by.map((lock) => <li key={lock}>{lock}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className={s.filterRow}>
        {[['open', `To decide (${open.length})`],
          ['watch', `Watching (${watching.length})`],
          ['decided', `Decided (${decided.length})`]].map(([key, label]) => (
          <button key={key} className={showing === key ? s.chipActive : s.chip}
                  onClick={() => { setShowing(key); setSelected([]); }}>
            {label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#6B7280' }}>
          {money(data?.total_impact, currency)} of measurable money across the open list
        </span>
      </div>

      <div className={s.filterRow}>
        {features.map((f) => (
          <button key={f} className={feature === f ? s.chipActive : s.chip}
                  onClick={() => setFeature(f)}>
            {f === 'all' ? 'Everything' : FEATURE_LABEL[f]}
            {showing === 'open' && <span className={s.chipCount}>{counts[f]}</span>}
          </button>
        ))}
      </div>

      {showing === 'open' && selected.length > 0 && (
        <div className={s.bulkBar}>
          <span className={s.bulkCount}>{selected.length} selected</span>
          <button className={s.approve} disabled={busy}
                  onClick={() => { onApproveMany(selected); setSelected([]); }}>
            <FiCheck /> Approve {selected.length}
          </button>
          <button className={s.linkButton} onClick={() => setSelected([])}>Clear</button>
        </div>
      )}

      <div className={s.findingList}>
        {rows.map((x) => (
          <article key={x.id}
                   className={`${s.suggestion} ${s[`suggestion_${x.status === 'Watch'
                     ? 'Watch' : x.priority}`]} ${s[`suggestion_${x.status}`] || ''}`}>
            <div className={s.sugHead}>
              {showing === 'open' && (
                <input type="checkbox" checked={selected.includes(x.id)}
                       onChange={() => toggle(x.id)} aria-label="select" />
              )}
              <span className={s.sugFeature}>{FEATURE_LABEL[x.feature] || x.feature}</span>
              <span className={s.sugHeadline}>{x.headline}</span>
              {x.impact != null
                ? <span className={s.sugImpact}>{money(x.impact, currency)}</span>
                : <span className={s.findingNoImpact}>No rupee figure</span>}
            </div>

            <p className={s.sugWhy}>{x.why}</p>
            {x.impact_note && (
              <p className={s.sugWhy} style={{ color: '#7A8496', fontSize: 12.5 }}>
                {x.impact_note}
              </p>
            )}
            <Words action={x.action} />
            <Expected expected={x.expected} currency={currency} />

            {x.status === 'Watch' && x.watch && (
              <div className={s.sugWatch}>
                <FiEye /> {x.watch.reads}
                {x.watch.clicks_now != null
                  && ` It has ${x.watch.clicks_now} clicks so far.`}
              </div>
            )}
            {(x.warnings || []).map((w) => (
              <div key={w} className={s.sugWarn}>{w}</div>
            ))}
            {x.expired_why && <div className={s.sugWarn}>{x.expired_why}</div>}
            {x.failure && (
              <div className={s.sugWarn}>
                Amazon did not accept this: {x.failure.reason}
              </div>
            )}

            <div className={s.sugMeta}>
              {x.status !== 'Pending' && (
                <span className={`${s.statusPill} ${s[`statusPill_${x.status}`] || ''}`}>
                  {x.status}
                </span>
              )}
              {x.status === 'Pending' && <span>{x.priority} priority</span>}
              <Confidence value={x.confidence} parts={x.confidence_parts} />
              {x.prd_defined && (
                <span className={s.prdFlag}
                      title="Defined in the PRD and not yet moved to the DRD logic batch.">
                  PRD rule
                </span>
              )}
              <span className={s.sugActions}>
                {x.status === 'Pending' && (
                  <>
                    <button className={s.approve} disabled={busy}
                            onClick={() => onDecide(x, 'approve')}>
                      <FiCheck /> Approve
                    </button>
                    <button className={s.linkButton} disabled={busy}
                            onClick={() => onDecide(x, 'dismiss')}>
                      <FiX /> Not this one
                    </button>
                  </>
                )}
                {x.status === 'Approved' && (
                  <button className={s.linkButton} disabled={busy}
                          onClick={() => onApply(x)}>
                    See what would be sent
                  </button>
                )}
                {x.status === 'Applied' && (
                  <button className={s.linkButton} disabled={busy}
                          onClick={() => onUndo(x)}>
                    <FiRotateCcw /> Undo
                  </button>
                )}
                {['Dismissed', 'Expired'].includes(x.status) && (
                  <button className={s.linkButton} disabled={busy}
                          onClick={() => onDecide(x, 'reopen')}>
                    <FiRotateCcw /> Bring back
                  </button>
                )}
                {x.feature === 'conflicts' && x.scope?.pair && (
                  <button className={s.linkButton} disabled={busy}
                          onClick={() => onIntentional(x.scope.pair)}>
                    This is deliberate
                  </button>
                )}
              </span>
            </div>
          </article>
        ))}
        {!rows.length && (
          <p className={s.empty}>
            {showing === 'watch'
              ? 'Nothing is being watched.'
              : showing === 'decided' ? 'Nothing decided yet.'
                : 'Nothing to decide here.'}
          </p>
        )}
      </div>

      {data?.approval_note && (
        <p className={s.coverage} style={{ marginTop: 16 }}>{data.approval_note}</p>
      )}
    </div>
  );
}
